import { useMemo } from 'react';

import type { ClaimSideBarTab, SideBarKey } from '~/components/ClaimPage/hooks/useClaimSideBarTabs/utils';
import { tabKeyToTabGetter } from '~/components/ClaimPage/hooks/useClaimSideBarTabs/utils';
import type { UseCliveClaimSidebarTabsArgs } from '~/components/ClaimPage/types';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';

type CliveTabKey = Extract<SideBarKey, 'notes' | 'documents' | 'notifications' | 'clive_chat'>;

export type CliveClaimSideBarTab = ClaimSideBarTab<CliveTabKey>;

/**
 * Custom hook for CliveApp claim sidebar tabs
 * Returns Notes, Documents, Notifications, and Clive chat tabs for the simplified CliveApp claim page
 * Reuses the main tab definitions from tabKeyToTabGetter for consistency
 */
const CLIVE_TAB_KEYS: CliveTabKey[] = ['clive_chat', 'notifications', 'notes', 'documents'];

export const useCliveClaimSidebarTabs = ({
  userOrganization,
  claim,
}: UseCliveClaimSidebarTabsArgs): CliveClaimSideBarTab[] => {
  const { isClaimChatEnabled } = useCliveClaimContext();

  return useMemo(() => {
    // Reuse the existing tab definitions from the main claim page
    return CLIVE_TAB_KEYS.map((tabKey) =>
      tabKeyToTabGetter[tabKey]({
        userOrganization,
        claim,
        hasLegalActionsPermissions: false,
        legalActions: [],
        reloadLegalActions: () => Promise.resolve(),
        isClaimChatEnabled,
        // CLIVE_TAB_KEYS omits clive_insights, so its getter never runs here and none of these
        // values is ever read; the CliveApp claim page has no Insights tab to gate, badge or trim.
        isCliveInsightsTabEnabled: false,
        undismissedCount: 0,
        isConductorHubShown: false,
      })
    ).filter((tab) => tab.shouldDisplay);
  }, [userOrganization, claim, isClaimChatEnabled]);
};
